import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, MapPin, Loader2, Ticket, Clock, Ban, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const LABELS = {
  pending: { t: "Awaiting host", c: "text-amber-400" },
  booked: { t: "Confirmed", c: "text-brand" },
  attended: { t: "Attended", c: "text-green-400" },
  no_show: { t: "Missed", c: "text-red-400" },
  cancelled: { t: "Cancelled", c: "text-sec" },
  cancelled_late: { t: "Cancelled (late)", c: "text-red-400" },
  cancelled_emergency: { t: "Refunded (emergency)", c: "text-green-400" },
  host_cancelled: { t: "Host cancelled", c: "text-red-400" },
};

function countdown(startAt) {
  if (!startAt) return "Time TBA";
  const diff = new Date(startAt).getTime() - Date.now();
  if (isNaN(diff)) return "Time TBA";
  if (diff <= 0) return "Happening now / done";
  const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `in ${d}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  return `in ${m}m`;
}

export default function Bookings() {
  const [list, setList] = useState(null);
  const [tick, setTick] = useState(0);

  const load = () => api.get("/events/my/bookings").then(({ data }) => setList(data.bookings || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { const i = setInterval(() => setTick((t) => t + 1), 60000); return () => clearInterval(i); }, []);

  const cancel = async (b) => {
    try {
      const { data } = await api.post(`/events/${b.event_id}/cancel/${b.booking_id}`);
      toast.success(data.message || "Cancelled");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (list === null) return <div className="max-w-4xl mx-auto px-5 pt-28 flex items-center gap-2 text-sec"><Loader2 className="animate-spin" size={18} /> Loading your bookings…</div>;

  const active = list.filter((b) => ["pending", "booked"].includes(b.status));
  const past = list.filter((b) => !["pending", "booked"].includes(b.status));

  const Card = ({ b }) => {
    const lbl = LABELS[b.status] || { t: b.status, c: "text-sec" };
    const canCancel = ["pending", "booked"].includes(b.status) && !b.listing_cancelled;
    return (
      <div className="rud-card rounded-2xl overflow-hidden flex" data-testid={`booking-${b.booking_id}`}>
        {b.cover ? <img src={b.cover} alt="" className="w-28 h-full object-cover shrink-0" /> : <div className="w-2 bg-brand/40 shrink-0" />}
        <div className="p-4 flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-widest text-brand">{b.kind === "meal" ? "Meal" : "Event"} · {b.type}</span>
            <span className={`text-xs ${lbl.c}`} data-testid="booking-status">{lbl.t}</span>
          </div>
          <Link to={`/${b.kind === "meal" ? "meals" : "events"}`} className="font-serif text-lg leading-snug block mt-1 hover:text-brand">{b.title}</Link>
          <div className="text-xs text-sec mt-1 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><MapPin size={11} /> {b.address || "Nearby"}</span>
            <span className="flex items-center gap-1"><CalendarClock size={11} /> {b.when || "TBA"}</span>
            {["pending", "booked"].includes(b.status) && <span className="flex items-center gap-1 text-brand"><Clock size={11} /> {countdown(b.start_at)}</span>}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-sec">
              {b.kind === "meal" && b.status === "booked" ? `Paid ${b.paid} · ${b.balance} due after` : (b.price ? `${b.price} credits` : "Free")}
            </span>
            {canCancel && <button data-testid="booking-cancel" onClick={() => cancel(b)} className="text-xs rounded-full border border-red-500/40 text-red-400 px-3 py-1">{b.status === "pending" ? "Withdraw" : "Cancel"}</button>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-5 pt-24 pb-16" data-testid="bookings-page">
      <div className="flex items-center gap-2"><Ticket className="text-brand" /><h1 className="font-serif text-4xl tracking-tight">My Bookings</h1></div>
      <p className="text-sec text-sm mt-2">Every meal and event you've reserved, with live countdowns. Cancel free up to 9h before.</p>

      {list.length === 0 ? (
        <div className="mt-12 text-center" data-testid="bookings-empty">
          <Ticket className="text-brand/60 mx-auto" size={40} />
          <p className="text-sec mt-3">You haven't reserved anything yet.</p>
          <div className="flex gap-2 justify-center mt-4">
            <Link to="/meals" className="btn-primary rounded-full h-10 px-5 inline-flex items-center text-sm">Find a meal</Link>
            <Link to="/events" className="btn-ghost rounded-full h-10 px-5 inline-flex items-center text-sm">Find an event</Link>
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <section>
            <div className="text-xs uppercase tracking-widest text-sec mb-3 flex items-center gap-1"><CheckCircle2 size={13} /> Upcoming ({active.length})</div>
            {active.length === 0 ? <p className="text-sec text-sm">Nothing upcoming.</p> : <div className="grid sm:grid-cols-2 gap-3" data-testid="bookings-upcoming">{active.map((b) => <Card key={b.booking_id} b={b} />)}</div>}
          </section>
          {past.length > 0 && (
            <section>
              <div className="text-xs uppercase tracking-widest text-sec mb-3 flex items-center gap-1"><Ban size={13} /> Past & cancelled ({past.length})</div>
              <div className="grid sm:grid-cols-2 gap-3 opacity-80" data-testid="bookings-past">{past.map((b) => <Card key={b.booking_id} b={b} />)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
